import type {
	InputProps as AriaInputProps,
	TextFieldProps as AriaTextFieldProps,
} from "react-aria-components";

import { HelpCircle } from "@untitledui/icons";
import {
	type ComponentType,
	createContext,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	useContext,
} from "react";
import {
	Group as AriaGroup,
	Input as AriaInput,
	TextField as AriaTextField,
} from "react-aria-components";

import { HintText } from "@/components/ui/input/hint-text";
import { Label } from "@/components/ui/input/label";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx, sortCx } from "@/utils/cx";

export interface InputBaseProps extends TextFieldProps {
	groupRef?: Ref<HTMLDivElement>;
	/** Icon component to display on the left side of the input. */
	icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
	/** Class name for the icon. */
	iconClassName?: string;
	/** Class name for the input. */
	inputClassName?: string;
	/** Placeholder text. */
	placeholder?: string;
	ref?: Ref<HTMLInputElement>;
	/** Keyboard shortcut to display. */
	shortcut?: boolean | string;
	/**
	 * Input size.
	 * @default "sm"
	 */
	size?: "md" | "sm";
	/** Tooltip message on hover. */
	tooltip?: string;
	/** Class name for the tooltip. */
	tooltipClassName?: string;
	/** Class name for the input wrapper. */
	wrapperClassName?: string;
}

export const InputBase = ({
	groupRef,
	icon: Icon,
	iconClassName,
	inputClassName,
	isDisabled,
	isInvalid,
	// Omit this prop to avoid invalid HTML attribute warning
	isRequired: _isRequired,
	placeholder,
	ref,
	shortcut,
	size = "sm",
	tooltip,
	tooltipClassName,
	wrapperClassName,
	...inputProps
}: Omit<InputBaseProps, "hint" | "label">) => {
	// Check if the input has a leading icon or tooltip
	const hasTrailingIcon = tooltip || isInvalid;
	const hasLeadingIcon = Icon;

	// If the input is inside a `TextFieldContext`, use its context to simplify applying styles
	const context = useContext(TextFieldContext);

	const inputSize = context?.size || size;

	const sizes = sortCx({
		md: {
			iconLeading: "left-3.5",
			iconTrailing: "right-3.5",
			root: cx(
				"px-3.5 py-2.5",
				hasTrailingIcon && "pr-9.5",
				hasLeadingIcon && "pl-10.5",
			),
			shortcut: "pr-3",
		},
		sm: {
			iconLeading: "left-3",
			iconTrailing: "right-3",
			root: cx(
				"px-3 py-2",
				hasTrailingIcon && "pr-9",
				hasLeadingIcon && "pl-10",
			),
			shortcut: "pr-2.5",
		},
	});

	return (
		<AriaGroup
			{...{ isDisabled, isInvalid }}
			className={({ isDisabled, isFocusWithin, isInvalid }) =>
				cx(
					"relative flex w-full flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset",

					isFocusWithin && !isDisabled && "ring-2 ring-brand",

					// Disabled state styles
					isDisabled && "cursor-not-allowed text-placeholder opacity-50",
					"group-disabled:cursor-not-allowed group-disabled:opacity-50",

					// Invalid state styles
					isInvalid && "ring-error_subtle",
					"group-invalid:ring-error_subtle",

					// Invalid state with focus-within styles
					isInvalid && isFocusWithin && "ring-1 ring-error",
					isFocusWithin && "group-invalid:ring-1 group-invalid:ring-error",

					context?.wrapperClassName,
					wrapperClassName,
				)
			}
			ref={groupRef}
		>
			{/* Leading icon and Payment icon */}
			{Icon && (
				<Icon
					className={cx(
						"pointer-events-none absolute size-5 text-fg-quaternary",
						sizes[inputSize].iconLeading,
						context?.iconClassName,
						iconClassName,
					)}
				/>
			)}

			{/* Input field */}
			<AriaInput
				{...(inputProps as AriaInputProps)}
				className={cx(
					"m-0 w-full bg-transparent text-md text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary",
					isDisabled && "cursor-not-allowed text-placeholder",
					sizes[inputSize].root,
					context?.inputClassName,
					inputClassName,
				)}
				placeholder={placeholder}
				ref={ref}
			/>

			{/* Tooltip and help icon */}
			{tooltip && !isInvalid && (
				<Tooltip placement="top" title={tooltip}>
					<TooltipTrigger
						className={cx(
							"absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover",
							sizes[inputSize].iconTrailing,
							context?.tooltipClassName,
							tooltipClassName,
						)}
					>
						<HelpCircle className="size-4" />
					</TooltipTrigger>
				</Tooltip>
			)}

			{/* Shortcut */}
			{shortcut && (
				<div
					className={cx(
						"pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8",
						sizes[inputSize].shortcut,
					)}
				>
					<span
						aria-hidden="true"
						className={cx(
							"pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset",
							isDisabled && "bg-transparent",
						)}
					>
						{typeof shortcut === "string" ? shortcut : "⌘K"}
					</span>
				</div>
			)}
		</AriaGroup>
	);
};

InputBase.displayName = "InputBase";

interface BaseProps {
	/** Error message displayed below the input */
	errorMessage?: ReactNode;
	/** Helper text displayed below the input */
	hint?: ReactNode;
	/** Label text for the input */
	label?: string;
}

interface TextFieldProps
	extends
		AriaTextFieldProps,
		BaseProps,
		Pick<
			InputBaseProps,
			| "iconClassName"
			| "inputClassName"
			| "size"
			| "tooltipClassName"
			| "wrapperClassName"
		> {
	ref?: Ref<HTMLDivElement>;
}

const TextFieldContext = createContext<TextFieldProps>({});

export const TextField = ({ className, ...props }: TextFieldProps) => {
	return (
		<TextFieldContext.Provider value={props}>
			<AriaTextField
				{...props}
				className={(state) =>
					cx(
						"group flex h-max w-auto flex-col items-start justify-start gap-1.5",
						typeof className === "function" ? className(state) : className,
					)
				}
				data-input-wrapper
			/>
		</TextFieldContext.Provider>
	);
};

TextField.displayName = "TextField";

interface InputProps extends BaseProps, InputBaseProps {
	/** Whether to hide required indicator from label */
	hideRequiredIndicator?: boolean;
}

export const Input = ({
	className,
	errorMessage,
	groupRef,
	hideRequiredIndicator,
	hint,
	icon: Icon,
	iconClassName,
	inputClassName,
	label,
	placeholder,
	ref,
	shortcut,
	size = "sm",
	tooltip,
	tooltipClassName,
	wrapperClassName,
	...props
}: InputProps) => {
	return (
		<TextField
			aria-label={!label ? placeholder : undefined}
			{...props}
			className={className}
		>
			{({ isDisabled, isInvalid, isRequired }) => (
				<>
					{label && (
						<Label
							isRequired={
								hideRequiredIndicator ? !hideRequiredIndicator : isRequired
							}
						>
							{label}
						</Label>
					)}

					<InputBase
						{...{
							groupRef,
							icon: Icon,
							iconClassName,
							inputClassName,
							isDisabled,
							isInvalid,
							placeholder,
							ref,
							shortcut,
							size,
							tooltip,
							tooltipClassName,
							wrapperClassName,
						}}
					/>

					{hint && !errorMessage && !isInvalid && (
						<HintText isInvalid={isInvalid}>{hint}</HintText>
					)}
					{errorMessage && isInvalid && (
						<HintText isInvalid={isInvalid}>{errorMessage}</HintText>
					)}
				</>
			)}
		</TextField>
	);
};

Input.displayName = "Input";
